import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
}

export function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

export function formatRelativeTime(timestamp: string | number | null | undefined): string {
  if (!timestamp) return 'Never';
  const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatDate(timestamp: string | number): string {
  const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export const AGENT_META: Record<string, { label: string; emoji: string; description: string; category: string }> = {
  chief_of_staff: { label: 'Chief of Staff', emoji: '🎯', description: 'Coordinates all agents and provides strategic oversight', category: 'Leadership' },
  sales_lead: { label: 'Sales Lead', emoji: '💼', description: 'Manages the sales team and pipeline strategy', category: 'Sales' },
  lead_hunter: { label: 'Lead Hunter', emoji: '🎣', description: 'Finds and qualifies new leads 24/7', category: 'Sales' },
  outbound_rep: { label: 'Outbound Rep', emoji: '📞', description: 'Runs cold outreach campaigns via email and phone', category: 'Sales' },
  inbound_closer: { label: 'Inbound Closer', emoji: '🤝', description: 'Handles inbound calls and closes warm leads', category: 'Sales' },
  deal_reviver: { label: 'Deal Reviver', emoji: '🔄', description: 'Resurrects stalled deals with targeted outreach', category: 'Sales' },
  marketing_lead: { label: 'Marketing Lead', emoji: '📣', description: 'Directs marketing strategy and campaign coordination', category: 'Marketing' },
  content_creator: { label: 'Content Creator', emoji: '✍️', description: 'Creates blog posts, emails, and marketing copy', category: 'Marketing' },
  social_manager: { label: 'Social Manager', emoji: '📱', description: 'Manages social media presence and engagement', category: 'Marketing' },
  growth_scout: { label: 'Growth Scout', emoji: '🚀', description: 'Identifies and tests new growth channels', category: 'Marketing' },
  campaign_builder: { label: 'Campaign Builder', emoji: '🎨', description: 'Designs and launches full marketing campaigns', category: 'Marketing' },
  support_lead: { label: 'Support Lead', emoji: '🛡️', description: 'Manages customer support quality and escalations', category: 'Support' },
  ticket_responder: { label: 'Ticket Responder', emoji: '🎫', description: 'Responds to support tickets autonomously', category: 'Support' },
  faq_bot: { label: 'FAQ Bot', emoji: '💬', description: 'Answers common questions from the knowledge base', category: 'Support' },
  finance_lead: { label: 'Finance Lead', emoji: '💰', description: 'Oversees financial health and cash flow', category: 'Finance' },
  invoice_chaser: { label: 'Invoice Chaser', emoji: '📄', description: 'Follows up on unpaid invoices automatically', category: 'Finance' },
  expense_categorizer: { label: 'Expense Categorizer', emoji: '🧾', description: 'Categorizes and analyzes business expenses', category: 'Finance' },
  intelligence_lead: { label: 'Intelligence Lead', emoji: '🧠', description: 'Synthesizes market intelligence and competitive data', category: 'Intelligence' },
  competitor_analyst: { label: 'Competitor Analyst', emoji: '🔍', description: 'Monitors competitors and finds differentiation opportunities', category: 'Intelligence' },
  price_monitor: { label: 'Price Monitor', emoji: '💹', description: 'Tracks competitor pricing and alerts on changes', category: 'Intelligence' },
  win_strategist: { label: 'Win Strategist', emoji: '♟️', description: 'Analyzes lost deals and builds winning battle cards', category: 'Intelligence' },
  data_miner: { label: 'Data Miner', emoji: '⛏️', description: 'Mines market data and business signals from the web', category: 'Intelligence' },
  trend_spotter: { label: 'Trend Spotter', emoji: '📈', description: 'Spots emerging trends before your competitors', category: 'Intelligence' },
  insight_engine: { label: 'Insight Engine', emoji: '💡', description: 'Synthesizes all data into strategic recommendations', category: 'Intelligence' },
  shopify_lead: { label: 'Shopify Lead', emoji: '🛍️', description: 'Manages Shopify store health and coordinates e-commerce agents', category: 'E-Commerce' },
  cart_recovery: { label: 'Cart Recovery', emoji: '🛒', description: 'Recovers abandoned carts with personalized sequences', category: 'E-Commerce' },
  product_copywriter: { label: 'Product Copywriter', emoji: '🖊️', description: 'Rewrites product descriptions to boost conversions', category: 'E-Commerce' },
  store_support: { label: 'Store Support', emoji: '🏪', description: 'Handles Shopify customer support with empathy', category: 'E-Commerce' },
  upsell_genius: { label: 'Upsell Genius', emoji: '⬆️', description: 'Maximizes revenue through post-purchase upsell sequences', category: 'E-Commerce' },
  customer_success: { label: 'Customer Success', emoji: '💚', description: 'Monitors customer health, prevents churn, and drives expansion revenue', category: 'Support' },
  team_coordinator: { label: 'Team Coordinator', emoji: '🏛️', description: 'Operations layer — runs standups, routes cross-team signals, reports to Chief of Staff', category: 'Leadership' },
};

export function getAgentMeta(agentType: string) {
  return AGENT_META[agentType] ?? { label: agentType, emoji: '🤖', description: 'AI Agent', category: 'Other' };
}

export const TIER_ORDER = ['free', 'starter', 'growth', 'pro', 'enterprise'];

export function tierLabel(tier: string): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

export function tierColor(tier: string): string {
  const map: Record<string, string> = {
    free: 'text-gray-400',
    starter: 'text-blue-400',
    growth: 'text-emerald-400',
    pro: 'text-violet-400',
    enterprise: 'text-amber-400',
  };
  return map[tier] ?? 'text-gray-400';
}

export function statusColor(status: string): string {
  const map: Record<string, string> = {
    active: 'text-emerald-400',
    idle: 'text-amber-400',
    paused: 'text-gray-400',
    uninitialized: 'text-gray-600',
    new: 'text-blue-400',
    contacted: 'text-violet-400',
    qualified: 'text-cyan-400',
    proposal: 'text-orange-400',
    won: 'text-emerald-400',
    lost: 'text-red-400',
  };
  return map[status] ?? 'text-gray-400';
}

export function truncate(str: string, maxLen: number): string {
  return str.length > maxLen ? `${str.slice(0, maxLen - 3)}...` : str;
}

export function groupBy<T>(arr: T[], key: keyof T): Record<string, T[]> {
  return arr.reduce((acc, item) => {
    const k = String(item[key]);
    if (!acc[k]) acc[k] = [];
    acc[k].push(item);
    return acc;
  }, {} as Record<string, T[]>);
}
